import { Component, Inject, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import { NgxRolesService } from 'ngx-permissions'
import { map, startWith } from 'rxjs/operators'
import * as fromRoot from '@tv3/store/state'
import { holdEventTypes } from '@tv3/constants/hold-event-types'
import { asLocalDate, selectAllRentals } from '@tokeet/tv3-platform'
import { SaveForm } from '@tokeet/tv3-platform'
import { HoldUpdateForm } from '@tv3/interfaces/forms/hold-update.form'
import { ConfirmDialogService } from '@tokeet/tv3-platform'
import { DeleteHoldEvent, UpdateHoldEvent } from '@tv3/store/calendar/calendar.actions'
import { CalendarEvent } from '@tv3/store/calendar/calendar.model'
import { AuthService } from '@tv3/services/auth.service'
import { Rental } from '@tokeet/tv3-platform'
import * as moment from 'moment'
import { InquirySharedDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-shared-dialogs.service'
import { Destroyable, untilDestroy } from '@tokeet/tv3-platform'
import { BehaviorSubject, merge } from 'rxjs'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatCheckboxChange } from '@angular/material/checkbox'

export interface EditHoldDialogInput {
  hold: CalendarEvent
  deletable?: boolean
  convertable?: boolean
}

@Component({
  selector: 'app-edit-hold-dialog',
  templateUrl: './edit-hold-dialog.component.html',
  styleUrls: ['./edit-hold-dialog.component.scss'],
  // tslint:disable-next-line use-host-property-decorator
  host: { class: 'modal-content' },
})
export class EditHoldDialogComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    title: ['', [Validators.required]],
    type: ['', [Validators.required]],
    start: ['', [Validators.required]],
    end: ['', [Validators.required]],
    expires: [''],
    rolling: [],
    rental: ['', [Validators.required]],
    color: [],
  })

  minDateDefault = moment().subtract(10, 'years').toDate()
  maxDateDefault = moment().add(10, 'years').toDate()

  types = holdEventTypes
  rentals$ = this.store.select(selectAllRentals).pipe(map((rentals) => this.filterRentals(rentals)))

  minExpirationDate = moment().startOf('day').toDate()

  minEndDate = new BehaviorSubject<Date>(null)
  maxStartDate = new BehaviorSubject<Date>(null)

  constructor(
    public dialogRef: MatDialogRef<EditHoldDialogComponent>,
    private store: Store<fromRoot.State>,
    private inquirySharedDialogsService: InquirySharedDialogsService,
    private confirmDialog: ConfirmDialogService,
    private fb: FormBuilder,
    private roleService: NgxRolesService,
    private auth: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: EditHoldDialogInput
  ) {
    super()
  }

  ngOnInit() {
    this.form.setValue({
      title: this.data.hold.title,
      type: this.data.hold.type,
      start: asLocalDate(this.data.hold.start),
      end: asLocalDate(this.data.hold.end),
      rolling: this.data.hold.rolling || 0,
      expires: this.data.hold.expires ? asLocalDate(this.data.hold.expires) : '',
      rental: this.data.hold.rentalId,
      color: this.data.hold.color,
    })

    merge(this.form.get('start').valueChanges, this.form.get('end').valueChanges)
      .pipe(startWith(undefined), untilDestroy(this))
      .subscribe(() => {
        this.setStartEndMinMax()
      })
  }

  setStartEndMinMax() {
    if (this.form.get('start').value) {
      const start = moment(this.form.get('start').value).add(1, 'days')
      this.minEndDate.next(start.toDate())
    }

    if (this.form.get('end').value) {
      const end = moment(this.form.get('end').value).subtract(1, 'days')
      this.maxStartDate.next(end.toDate())
    }
  }

  isOwner() {
    return this.roleService.getRole('OWNER')
  }

  filterRentals(rentals: Rental[]) {
    if (this.isOwner()) {
      rentals = rentals.filter((rental) => (rental.owners || []).includes(this.auth.user.id))
    }

    return rentals
  }

  close() {
    this.dialogRef.close()
  }

  convert() {
    const formValues = this.form.getRawValue()

    this.inquirySharedDialogsService.openAddInquiry({
      title: 'Create Booking',
      start: formValues.start,
      end: formValues.end,
      isBooking: true,
      convertFromHold: this.data.hold.id,
      defaults: {
        arriveDate: formValues.start,
        departDate: formValues.end,
        rentalId: formValues.rental,
        color: formValues.color,
      },
    })

    this.close()
  }

  onRollingChanged({ checked }: MatCheckboxChange) {
    this.form.patchValue({ rolling: !checked ? 0 : moment().unix() })
  }

  delete() {
    this.confirmDialog.confirm().subscribe(() => {
      this.store.dispatch(DeleteHoldEvent({ id: this.data.hold.id }))
      this.close()
    })
  }

  onChangeColor(color) {
    this.form.patchValue({ color })
  }

  @SaveForm()
  create(form: FormGroup) {
    const values = form.getRawValue() as HoldUpdateForm

    this.store.dispatch(UpdateHoldEvent({ form: values, hold: this.data.hold }))

    this.close()
  }
}
